import '@principal/design-system-icons-web/check';
import { html, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsFormElement } from '../pds-form-element/PdsFormElement';
import styles from './checkbox.scss?inline';

/**
 * @summary A checkbox is an input control that allows the user to select one or multiple choices from a list of options.
 *
 * @slot label Optional: Use this slot instead of the label property, if the label requires additional markup.
 * @slot help-text Optional: Use this slot instead of the helpText property, if the help text requires additional markup.
 */
@customElement('pds-checkbox', {
  category: 'component',
  type: 'component',
  styles,
})
export class PdsCheckbox extends PdsFormElement {
  /**
   * Style variant
   * - **default** renders the checkbox using no background
   * - **inverted** renders the checkbox using the inverted background token
   */
  @property()
  variant: 'inverted' | 'default' = 'default';

  /**
   * @internal
   *
   * Tells consuming applications that this component is type checkbox.
   */
  readonly type = 'checkbox';

  /**
   * @internal
   */
  defaultChecked: boolean;

  /**
   * @internal
   * needed to rerender the component when the hidden checkbox input gets or loses focus
   * managing this via component state and the .pds-is-focused class is simpler
   * than managing this via complicated SCSS selectors
   */
  @state()
  private isFocused: boolean = false;

  // need to override the `value` setter and getter
  // because checkbox works differently
  override set value(newValue) {
    this.internalValue = newValue;

    // set the value on the field
    if (this.field && this.field.value !== newValue) {
      this.field.value = newValue;
    }
  }

  /**
   * The value tied to the checkbox.
   */
  @property()
  override get value() {
    return this.internalValue;
  }

  set checked(newChecked: boolean) {
    // ariaChecked needs to be a string
    this.internals.ariaChecked = newChecked ? 'true' : 'false';

    if (newChecked) {
      // standard HTML form behavior adds the checkbox name
      // with its accompanying value to form data,
      // if the checkbox is checked
      // if the checkbox does not have a value property defined,
      // then it adds the checkbox name with the string 'on'
      this.internals.setFormValue(this.value || 'on');
    } else {
      // if the checkbox is not checked, then remove from form data
      this.internals.setFormValue(null);
    }
  }

  /**
   * Indicates whether the checkbox is checked.
   */
  @property({
    type: Boolean,
  })
  get checked(): boolean {
    return this.internals.ariaChecked === 'true';
  }

  /**
   * @internal
   * error
   * - Renders the checkbox as error - should only be set by the pds-checkbox-group
   */
  @property({
    type: Boolean,
  })
  error: boolean;

  /** @internal */
  @query('input')
  field: HTMLInputElement;

  protected override firstUpdated() {
    super.firstUpdated();
    this.setErrorState();
    this.defaultChecked = this.checked || this.hasAttribute('checked');
  }

  updated() {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.checked
      ? this.setAttribute('checked', '')
      : this.removeAttribute('checked');
  }

  setErrorState() {
    if (this.errorMessage) {
      this.error = true;
    } else {
      this.error = false;
    }
  }

  /**
   * Reset the input checked back it to its original checked attribute
   */
  formResetCallback() {
    this.checked = this.defaultChecked;
  }

  private handleChange() {
    this.checked = this.field.checked;

    this.dispatchEvent(
      new Event('pds-checkbox-change', { bubbles: true, composed: true }),
    );
  }

  private handleBlur() {
    this.isFocused = false;
    this.dispatchEvent(
      new Event('pds-checkbox-blur', { bubbles: false, composed: true }),
    );
  }

  private handleFocus() {
    this.isFocused = true;
    this.dispatchEvent(
      new Event('pds-checkbox-focus', { bubbles: false, composed: true }),
    );
  }

  override labelTemplate() {
    return html` <label
      for="${this.fieldId || `${this.name}-${this.randomId}-field`}"
      class="${this.classEl('label')}"
    >
      <span class="${this.classEl('box')}">
        <pds-icon-check
          class="${this.classEl('checkmark')}"
          size="sm"
        ></pds-icon-check>
      </span>
      <span class="${this.classEl('label-text')}">
        <slot name="label">${this.label}</slot>
        ${this.required
          ? html`<span
              class="${this.classEl('required-indicator')}"
              aria-hidden="true"
            >
              *
            </span>`
          : nothing}
      </span>
    </label>`;
  }

  /** @internal */
  get classNames() {
    return {
      [`${this.variant}`]: !!this.variant,
      'is-checked': this.checked,
      'is-error': this.error,
      'is-disabled': this.disabled,
      'hidden-label': this.hideLabel,
      'is-focused': this.isFocused,
    };
  }

  render() {
    if (!this.hasLabel) {
      return nothing;
    }

    // We add a pds-u-sr-only class to hide the browser's default checkbox, but keep it accessible to screen readers
    return html`<div class="${this.getClass()}">
      <input
        class="${this.classEl('input')} pds-u-sr-only"
        type="checkbox"
        name="${this.name}"
        id="${this.fieldId || `${this.name}-${this.randomId}-field`}"
        ?disabled=${this.disabled}
        ?checked=${this.checked}
        ?required=${this.required}
        aria-describedby=${this.getAriaDescribedBy() || nothing}
        aria-invalid=${this.error}
        value="${this.value || nothing}"
        @click=${this.handleChange}
        @change=${this.handleChange}
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
      />
      ${this.labelTemplate()}
      <div>${this.errorMessageTemplate()}</div>
    </div>`;
  }
}
